import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { coreSync } from '../lib/core';
import { ANALYTICS, trackEvent } from '../lib/analytics';
import { attendanceClearDocuments, attendanceDocuments, tombstoneDocument } from '../domain/lessonAttendance';
import { useCollectionWindow } from './useCollectionWindow';

// Taking attendance is the single most important write in the product, and the
// one most likely to happen offline — a teacher marks the lesson from a phone in
// a studio with no signal, and may well do it twice from two devices.
//
// One call produces up to six documents:
//   1. the attendance record            att_<occ>__<student>
//   2. the charge it implies            chg_<occ>__<student>
//   3. the make-up credit it grants     mk_<occ>__<student>
//   4. the make-up credit it spends     mkc_<occ>__<student>
//   5. the prepaid lesson it spends     pkg_<occ>__<student>
//   6. the payment taken at the lesson  pay_<occ>__<student>
//
// Every ID is derived from the same two facts, so the same decision made on two
// devices writes the same documents rather than twice as many. And they all go
// in one writeBatch, which — unlike runTransaction — commits offline, so the set
// can never partially apply and leave a charge without its attendance, or a
// payment without the charge it settles.

const AttendanceContext = createContext(null);
const TRACK_LIMIT = 5000;

export function AttendanceProvider({ children }) {
  const { user } = useAuth();

  // The attendance window, from whichever read path serves this session
  // (#207 U5). `withMetadata` keeps the snapshot path's `includeMetadataChanges`.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.attendance,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const records = read.docs;
  const loading = read.loading;

  const byKey = useMemo(() => {
    const map = new Map();
    for (const r of records) map.set(r.id, r);
    return map;
  }, [records]);

  const value = useMemo(
    () => ({
      loading,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: read.failed,
      records,

      statusFor: (occId, studentId) =>
        byKey.get(coreSync().attendanceId(occId, studentId))?.status
          || coreSync().defaultAttendanceStatus(),

      recordFor: (occId, studentId) => byKey.get(coreSync().attendanceId(occId, studentId)) || null,

      // occurrence: { id, date, durationMin, allDay } — `allDay` (#322),
      //           because an hourly student on a day-long event has no
      //           duration to be priced from. Absent means false.
      // student:    { id, familyId, pricingMode, rateMinor }
      //           — the student's `durationMin` is **not read here, and not
      //             passed** (#352, #374, #365): the calendar fold cannot hand
      //             over an occurrence with no duration, so a fallback to it
      //             would be unreachable.
      // packageRemaining: prepaid **minutes** the student still has (#181).
      //           Passed in rather than read here so this provider stays
      //           independent of usePackages and the caller decides which
      //           student's balance applies.
      // notes:   { student, parent, private } — three separate bodies, one per
      //           audience, which is how MMS models them: switching audience in
      //           its editor changes the placeholder and the visibility line, so
      //           they are distinct notes rather than one note with a flag.
      // payment:  { paid, amountMinor, note } — "student paid at lesson".
      // resourceIds: the lesson's Linked Online Resources (#424), `files` row
      //           ids in the order linked; null leaves the stored list alone.
      setAttendance: async ({
        occurrence, student, status,
        priceOverrideMinor = null, packageRemaining = 0,
        notes = null, payment = null, resourceIds = null, followOn = null,
      }) => {
        if (!user) return;

        // ── What the six documents are, and why ─────────────────────────
        //
        // The documents themselves are built by `attendanceDocuments` in
        // src/domain/lessonAttendance.js, so that a phone writes the same six
        // from the same rules; this provider keeps the batch:
        //
        //   * **the attendance record** — three plain strings of Markdown
        //     source, rendered at read time (src/domain/markdown.js,
        //     docs/features/lesson-note-markdown.md), never HTML;
        //   * **the charge** — `priceOccurrence` returns nothing for a
        //     fixed-monthly student and for an hourly one on an all-day event
        //     (#322), and `!= null` reads both; `isBillableFor` rather than the
        //     status's billing, because an event requiring a make-up credit
        //     unbills `Unrecorded` (#362);
        //   * **the prepaid time** — spent before money is charged, MMS's rule,
        //     and the two are mutually exclusive and both idempotent; minutes on
        //     every kind (#181);
        //   * **the payment at the lesson** — a real payment, not a suppressed
        //     charge, so a short one leaves the remainder owing;
        //   * **the make-up credit issued, and the one spent** (#303) — two
        //     documents with opposite deltas, each carrying the occurrence's
        //     own duration and nothing else (#352, #374, #365): the calendar
        //     fold cannot produce a zero, so there is no fallback to the
        //     student's length, and a credit the writer cannot measure is
        //     `null` and worth nothing.
        //
        // **Every document is written whole, every time, with `voided`
        // carrying the meaning.** A merge-set of a bare `{ voidedAt }` against
        // a document that does not exist yet is a *create* with no kind, no
        // amount and no family; the rules refuse it, and because these share a
        // batch the refusal would take the attendance record down with it. Both
        // halves of the tombstone go together: `voided` is the one the folds
        // read, because `voidedAt` is a serverTimestamp() that reads back null
        // from this device's own cache until the server acknowledges it (#70).
        //
        // No cap and no refusal on spending a credit the student does not
        // have: the gate is the dropdown (`attendanceStatusesFor`), a view of
        // the fold. A refusal here would refuse the whole decision, offline,
        // against a possibly-stale balance.
        const batch = writeBatch(db);
        const documents = attendanceDocuments({
          occurrence, student, status, priceOverrideMinor, packageRemaining,
          notes, payment, resourceIds, uid: user.uid,
        }, serverTimestamp());
        for (const { collection, id, data } of documents) {
          batch.set(docRef(user.uid, COLLECTIONS[collection], id), data, { merge: true });
        }

        // `followOn` (#590): the messages this Save asked for, as an
        // uncommitted batch. Queued by the write layer once this batch is
        // acknowledged and never if it is refused — see `releaseFollowOn` in
        // writes.js — and written onto this batch's durable note, so a page
        // load after the click cannot separate them.
        await batch.commit({ followOn });
        // Two yes/nos and never the notes or the amount (#458).
        trackEvent(ANALYTICS.attendanceMarked, {
          has_notes: Boolean(notes && ['student', 'parent', 'private'].some((k) => String(notes[k] ?? '').trim())),
          paid_at_lesson: payment?.paid === true,
        });
      },

      // Retracting what an attendance decision produced, because the lesson it
      // was produced from is being deleted off the calendar (#157).
      //
      // Why this reverses rather than refusing or cascading, and what is in the
      // set, is at the top of domain/lessonRemoval.js: the charge, the prepaid
      // consume and the make-up credit are retracted; the payment taken at the
      // lesson never is, matching `clearAttendance` below; and the attendance
      // record is untouched, since it is not money and holds the lesson notes.
      //
      // Tombstoned rather than deleted, as every retraction in this layer is: a
      // device that was offline for the delete still holds these documents and
      // would restore them from its own cache.
      //
      // **Ids only, and only ids the caller could see.** A merge against a
      // document that does not exist is a create, and a create carrying only
      // `{ voided, voidedAt }` has no kind, no amount and no family — the rules
      // reject it, and one rejection sinks the whole batch. `removalFootprint`
      // reports live documents, so every ref here is a document that exists.
      //
      // Chunked at 400 because a Firestore batch holds 500 writes and a "this
      // and following" delete over a long-taught series can pass that. The
      // chunks are separate commits; every write is idempotent, so running the
      // retraction again finishes it — as `relabelCategory` in useEvents.jsx.
      voidAttendanceEffects: async ({ ledgerIds = [], packageIds = [], makeupIds = [] }) => {
        if (!user) return 0;
        const refs = [
          ...ledgerIds.map((id) => docRef(user.uid, COLLECTIONS.ledgerEntries, id)),
          ...packageIds.map((id) => docRef(user.uid, COLLECTIONS.packageEntries, id)),
          ...makeupIds.map((id) => docRef(user.uid, COLLECTIONS.makeupEntries, id)),
        ];
        for (let i = 0; i < refs.length; i += 400) {
          const batch = writeBatch(db);
          for (const ref of refs.slice(i, i + 400)) {
            // Both halves of the tombstone. `voided` is the one every fold
            // reads, because it has a value the instant it is written;
            // `voidedAt` is a serverTimestamp() and reads back null from this
            // device's own cache until the server acknowledges it (#70), and
            // firestore.rules' `tombstoneAgrees` refuses one without the other.
            batch.set(ref, tombstoneDocument(serverTimestamp()), { merge: true });
          }
          await batch.commit();
        }
        return refs.length;
      },

      // Clearing attendance back to unrecorded. Unrecorded is itself billable,
      // so this restores the charge rather than removing it.
      //
      // Does not retract a payment taken at the lesson: money handed over is a
      // fact about the world, not a consequence of how the lesson was later
      // marked. Voiding it is a deliberate act on the ledger.
      //
      // **What it writes is narrower than "restores the charge"**: the
      // record's status and both make-up entries retracted — `unrecorded`
      // neither issues nor spends, so a lesson cleared back to Unrecorded hands
      // a spent credit back exactly as it takes an issued one away (#303) — but
      // no charge and no prepaid consume, so those stay as the last save left
      // them. #767 asks which a clear should be.
      //
      // The three documents are the core's (#765: `attendance_clear_writes` in
      // core/src/lesson.rs, through `attendanceClearDocuments`), so a Cloud
      // Function clears exactly as this does;
      // `core/src/attendance_clear_vectors.json` holds the recorded answers.
      clearAttendance: async ({ occurrence, student }) => {
        if (!user) return;
        const batch = writeBatch(db);
        const documents = attendanceClearDocuments({ occurrence, student, uid: user.uid }, serverTimestamp());
        for (const { collection, id, data } of documents) {
          batch.set(docRef(user.uid, COLLECTIONS[collection], id), data, { merge: true });
        }
        await batch.commit();
      },
    }),
    [byKey, records, loading, read.failed, user]
  );

  return <AttendanceContext.Provider value={value}>{children}</AttendanceContext.Provider>;
}

export function useAttendance() {
  const ctx = useContext(AttendanceContext);
  if (!ctx) throw new Error('useAttendance must be used within an AttendanceProvider');
  return ctx;
}
